<template>
  <div class="order-details" :class="tab.id === 'shippingDetails' ? 'no-footer': ''">
    <div class="column">
      <div class="columns has-seperator">
        <div class="column pt-5 pb-5">
          <label class="has-text-black-bis is-italic is-size-3 line-height">
            Shipping Order
          </label>
          <input class="input generic-input" v-model="rowData.name" v-if="editMode"/>
          <div class="line-height generic-input is-size-3 field" v-else>
            <span>{{ rowData.name }}</span>
          </div>
        </div>
        <div class="column grouped-buttons">
          <div v-if="!editMode" class="is-flex is-justify-content-flex-end">
            <button class="button has-text-weight-bold has-background-black-bis has-text-white is-size-4"
              @click="deleteShipment" v-if="['not-started', 'un-scheduled'].includes(rowData.status)">
              Delete Order
            </button>          
              <div class="is-divider-vertical mt-0"></div>
              <button class="button px-0 has-text-dark" @click="setEditMode(true)">
                <i class="icon-edit" ></i>
              </button>
          </div>
          <div class="is-flex is-justify-content-flex-end" v-else>
            <button class="mr-2 button has-text-weight-bold has-background-white is-outlined is-size-4"
              @click="cancelEdit">
              Cancel
            </button>
            <button class="button has-text-weight-bold has-background-black-bis has-text-white is-size-4"
              @click="save">
              Save
            </button>
          </div>
        </div>
      </div>
      <div class="columns has-seperator mb-1" :key="reRender">
        <div class="column">
          <h4 class="is-italic is-size-3 is-italic has-text-weight-semibold">
            Origin Details
          </h4>
          <div class="columns is-multiline">
            <div class="column mt-2 is-8">
              <field-project-select
                titleLabel="Source Project"
                :value="rowData.delivery?.currentProject"
                :options="currentProjectsList"
                :isEditing="true"
                :isDisabled="true"
                @select="$_.set(sLabel, 'delivery.currentProject', $event)"
              >
              </field-project-select>
            </div>
            <div class="column is-8">
              <field-location-select
                titleLabel="Origin Location"
                :isEditing="true"
                :isDisabled="true"
                :value="rowData.delivery?.currentLocation"
                :options="projectLocations[rowData.delivery?.currentProject._id] || []"
                @update:value="(val) => $_.set(rowData, 'delivery.currentLocation', val)"
              ></field-location-select>
            </div>
            <div class="column mb-2 is-8">
              <field-user-select
                titleLabel="Sender"
                :isEditing="true"
                :disabled="!editMode"
                :key="allUsers.length"
                :options="allUsers"
                :value="rowData.delivery?.owner"
                @update:value="(val) => $_.set(rowData, 'delivery.owner', val)"
              ></field-user-select>
            </div>
          </div>
        </div>
        <div class="column">
          <h4 class="is-italic is-size-3 has-text-weight-semibold ">
            Recipient Details
          </h4>
          <div class="columns is-multiline">
            <div class="column mt-2 is-8">
              <field-project-select
                titleLabel="Destination Project"
                :value="getDeliveryProject()"
                :options="destProjList"
                :isEditing="true"
                @update:value="(v) => destinationProject(v)"
                @select="destProjectChanged($event)"
                :isDisabled="!editMode || rowData.status !== 'not-started'"
              >
              </field-project-select>
            </div>
            <div class="column is-8">
              <field-location-select
                :key="rowData.delivery?.deliveryProject._id"
                titleLabel="Destination Location"
                :isEditing="true"
                :options="deliveryProjectLocationsList || []"
                :value="rowData.delivery?.deliveryLocation"
                :projectId="rowData.delivery?.deliveryProject._id"
                :currentProjectId="rowData.delivery?.currentProject._id"
                :deliveryProjectId="rowData.delivery?.deliveryProject._id"
                :companyId="user.company"
                :shipFromInventory="shipFromInventory"
                @update:value="(val) => $_.set(rowData, 'delivery.deliveryLocation', val)"
                :isDisabled="!editMode || rowData.status !== 'not-started'"
              ></field-location-select>
            </div>
            <div class="column mb-2 is-8">
              <field-user-select
                titleLabel="Receiver"
                :isEditing="true"
                :key="allUsers.length"
                :options="allUsers"
                :value="rowData.delivery?.recipient"
                @update:value="(val) => $_.set(rowData, 'delivery.recipient', val)"
                :disabled="!editMode"
              ></field-user-select>
            </div>
          </div>
        </div>
      </div>
      <!-- Custom Fields Section start here -->
       <div class="shipping-cf">
        <custom-fields-order-view :card="rowData" :editMode="editMode"/>
       </div>
      <!-- Custom Fields Section ends here -->
      <div class="columns">
        <div class="column mt-2 pl-0">
          <field-file-list
            :order="rowData"
            prop="files"
            :isShipping="true"
            :isDisabled="editMode"
            @saveFiles="save"
            @close="cancelEdit"
            />
        </div>
        <div class="column mt-2">
          <order-notes
            :order="rowData"
            prop="notes"
            :isShipping="true"
            :disabled="editMode"
            :refreshFunction="refreshFunction"
            @update:value="updateNotes">
          </order-notes>
        </div>
        <div class="column mt-2">
          <order-notify :value="rowData.delivery.notify"
            :order="rowData"
            :disabled="editMode"
            :projectUsers="projectUsers[rowData.delivery?.deliveryProject._id]"
            @addNotifier="updateNotifiers"
            @saveUsers="save">
          </order-notify>
        </div>
      </div>
    </div>
    <o-loading :active="isLoading" :full-page="false"/>
  </div>
</template>

<script>
import _, { isUndefined, set } from 'lodash';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { toRefs, reactive, computed, inject, onBeforeMount } from 'vue';

import Users from '@/models/Users';
import Projects from '@/models/Projects';
import Locations from '@/models/Locations';
import UtilityMixin from '@/components/mixins/UtilityMixin';
import SelectStatus from '@/components/fields/selectStatus.vue';
import BaseCardColumn from '@/components/card-edit/BaseCardColumn.vue';
import OrderNotes from '@/components/OrderNotes.vue';
import OrderNotify from '@/components/OrderNotify.vue';
import Validations from '../utils/Validations';
import CustomFieldsOrderView from './card-edit/CustomFieldsOrderView.vue';

export default {
  name: 'Shipping Details',
  components: {
    'base-column': BaseCardColumn,
    SelectStatus,
    'order-notes': OrderNotes,
    'order-notify': OrderNotify,
    CustomFieldsOrderView,
  },
  props: {
    tab: Object,
    rowData: Object,
    shipFromInventory: Boolean,
  },
  setup(props) {
    const store = useStore();
    const route = useRoute();
    const { getAllLocations } = UtilityMixin();
    const shippingEvents = inject('shippingActionEvents')

    const data = reactive({
      user: {},
      tabName: '',
      reRender: 0,
      allUsers: [],
      editMode: false,
      allProjects: [],
      projectUsers: {},
      isLoading: false,
      projectLocations: [],
      commonStockProject: {},
      currentProjectsList: [],
      destinationProjects: [],
      deliveryProjectsList: [],
      deliveryProjectLocation: {},
      customFields: [],
    });

    data.user = store.state.userData;

    const projectId = route.params.projectId;

    const getDeliveryProject = () => {
      // if (isUndefined(props.rowData._id)
      //   && props.shipFromInventory
      //   && _.isEmpty(props.rowData.delivery?.deliveryProject) && !props.rowData.delivery?.currentProject.isCommonStockProject) {
      //   _.set(props.rowData, 'delivery.deliveryProject', props.rowData.delivery?.currentProject);
      //   projectChanged(props.rowData.delivery?.currentProject);
      //   return props.rowData.delivery?.currentProject;
      // }
      return props.rowData.delivery?.deliveryProject;
    };

    const destProjList = computed(() => {
      if (props.rowData.delivery.currentProject.isCommonStockProject) {
        return data.allProjects;
      }
      return data.destinationProjects;
    });

    const deliveryProjectLocationsList = computed(() => {
      if (data.deliveryProjectLocation) {
        return data.deliveryProjectLocation[props.rowData.delivery?.deliveryProject._id];
      } return [];
    });

    const fetchLocations = async (pId, isGIProject) => {
      if (_.isEmpty(store.state.userData?.company)) {
        await store.dispatch('getUserData');
      }
      let locs = await getAllLocations(pId);
      locs = _.filter(locs, (loc) => loc.company._id === _.get(store.state.userData, 'company._id', store.state.userData.company));
      if (isGIProject) {
        _.remove(locs, (loc) => loc.type !== 'gi_location');
      }
      // if (props.shipFromInventory) {
        locs = _.partition(locs, (loc) => loc.nestedLocation)[
          +(pId !== store.state.queryParams.commonStockProject._id)
        ];
      // }
      return Locations.groupLocations(locs, store.state.companyData);
    };

    const getProjectId = (project) => {
      if (
        props.rowData.delivery.currentProject._id === store.state.commonStockProject
      ) {
        return props.rowData.delivery.deliveryProject._id;
      }
      if (
        props.rowData.delivery.deliveryProject._id
        === store.state.commonStockProject
      ) {
        return props.rowData.delivery.currentProject._id;
      }
      return project._id;
    };
    const fetchUsers = async (pId) => {
      const allUsers = await Projects.linkedProjectUsers([pId]);
      const users = _.filter(allUsers, (usr) => usr.company === _.get(store.state.userData, 'company._id', store.state.userData.company));
      return users;
    };

    const projectChanged = async (project) => {
      if (project._id) {
        // set locations list
        if (_.isEmpty(data.projectLocations[project._id])) {
          data.projectLocations[project._id] = [];
          const locs = await fetchLocations(project._id, project.isGI);
          if (locs.length) data.projectLocations[project._id] = locs;
          
        }
        // set owners/recipients/also notify lists
        if (
          _.isEmpty(data.projectUsers[project._id])
          || props.rowData.delivery.currentProject._id
            === store.state.commonStockProject
        ) {
          const pId = getProjectId(project);
          data.projectUsers[projectId] = [];
          if (_.isEmpty(data.projectUsers[projectId])) {
            const users = await fetchUsers(pId);
            if (users.length) data.projectUsers[projectId] = users;
          }
        }
      }
    };

    const fetchProjects = async () => {
      // fetching and setting of projects lists happens here
      if (_.isEmpty(data.allProjects) ||
        _.isEmpty(data.currentProjectsList) ||
        _.isEmpty(data.deliveryProjectsList)
      ) {
        data.allProjects = store.state.queryParams._projectsAndGI;
        if (!data.allProjects.length) {
          const projects = await Promise.all([
            Projects.allProjects(),
            Projects.allProjects({ isGI: true }),
          ]);
          data.allProjects = _.flatMap(projects);
        }
        if (props.shipFromInventory) {
          const commonStockProject = await Projects.getCommonStockProject();
          data.allProjects.unshift(commonStockProject);
        }
        data.currentProjectsList = _.cloneDeep(data.allProjects);
        data.deliveryProjectsList = _.cloneDeep(data.allProjects);
      }
    };

    const destinationProject = async (v) => {
      _.set(props.rowData, 'delivery.deliveryProject', v);
      const users = await fetchUsers(props.rowData.delivery?.deliveryProject._id);
      data.allUsers = await Users.groupUsers(users);
    }

    const destProjectChanged = async (project) => {
      if (props.shipFromInventory && project._id) {
        // set locations list
        if (_.isEmpty(data.deliveryProjectLocation[project._id])) {
          _.set(data.deliveryProjectLocation, project._id, []);
          const locs = await fetchLocations(v);
          if (locs.length) data.deliveryProjectLocation[project._id] = locs;
        }
      }
    };

    const onEdit = async () => {
      await fetchProjects();
      await projectChanged(_.get(props.rowData, 'delivery.currentProject', {}));
      await projectChanged(_.get(props.rowData, 'delivery.deliveryProject', {}));
      if (_.isEmpty(data.commonStockProject)) {
        let csp = store.state.queryParams?.commonStockProject;
        csp = !_.isEmpty(csp) ? csp : await Projects.getCommonStockProject();
        data.commonStockProject = csp;
      }
      _.set(props.rowData, 'delivery.deliveryProject', props.rowData.delivery.deliveryProject);
      if (_.isEmpty(data.allUsers)) {
        const users = await fetchUsers(props.rowData.delivery?.deliveryProject._id);
        data.allUsers = await Users.groupUsers(users);
      }
    }

    const setEditMode = async (val) => {
      if (data.editMode) return;
      data.editMode = val;
      try {
        if (val) {
          data.isLoading = true;
          await onEdit();
          data.reRender++;
        }
      } catch (e) {
        console.log('Error: ', e)
      } finally {
        data.isLoading = false;
      }
    }

    const updateNotes = (notes) => {
      set(props.rowData, 'delivery.notes', notes);
      save();
    }

    const save = async () => {
      if (props.rowData.customFields) {
        let isMultiple;
        let isErrorMsg;
        const isValid = await Validations.validateCustomFields(props.rowData.customFields, props.rowData, data.user, isMultiple = true, 'sl_order', isErrorMsg = true);
        if (!isUndefined(isValid) && !isValid) return;
      }
      if (!_.isEmpty(shippingEvents) && _.isFunction(shippingEvents.save)) {
        await shippingEvents.save();
      }
      data.editMode = false;
    }

    const cancelEdit = async () => {
      data.editMode = false
      if (!_.isEmpty(shippingEvents) && _.isFunction(shippingEvents.discardChanges)) {
        await shippingEvents.discardChanges();
      }
    }

    const deleteShipment = async () => {
      if (!_.isEmpty(shippingEvents) && _.isFunction(shippingEvents.delete)) {
        await shippingEvents.delete();
      }
    }
    const updateNotifiers = (notifyUsers) => {
      set(props.rowData, 'delivery.notify', notifyUsers);
    };

    onBeforeMount( async () => {
      await projectChanged(_.get(props.rowData, 'delivery.currentProject', {}));
      await projectChanged(_.get(props.rowData, 'delivery.deliveryProject', {}));
    });

    return {
      ...toRefs(data),
      getDeliveryProject,
      destProjList,
      destinationProject,
      destProjectChanged,
      deliveryProjectLocationsList,
      setEditMode,
      save,
      cancelEdit,
      deleteShipment,
      updateNotes,
      updateNotifiers,
    };
  }
}
</script>

<style lang="scss" scoped>
  ::v-deep(.shipping-cf) {
    .multiselect__tags {
      .multiselect__placeholder {
        top: -1px;
        position: absolute;
      }
    }
  }
</style>>